import React, { useEffect } from 'react';
import { Edit, Brain, Target, BookOpen, Clock, Award } from 'lucide-react';
import { allMathTopics } from '../data/allMathTopics';

const MathTopicCard = ({ topic, onUseTemplate, onGenerateFlashCards, onStartQuiz }) => {
  // Re-render MathJax when component updates
  useEffect(() => {
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  }, [topic]);

  // Get full topic data from allMathTopics
  const fullTopic = allMathTopics[topic.id];
  const modulesList = fullTopic ? Object.values(fullTopic.modules || {}) : [];

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
      {/* Topic Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{topic.icon}</span>
            <div>
              <h3 className="text-lg font-semibold">{topic.id}</h3>
              <p className="text-sm opacity-90">{topic.titleEn}</p>
            </div>
          </div>
          <BookOpen className="w-6 h-6 opacity-80" />
        </div>
      </div>

      {/* Topic Content */}
      <div className="p-4">
        <h4 className="text-xl font-bold text-gray-800 mb-2">{topic.title}</h4>
        <p className="text-gray-600 text-sm mb-4">{topic.description}</p>

        {/* Modules Preview */}
        <div className="mb-4">
          <h5 className="text-sm font-semibold text-gray-700 mb-2">主要模組:</h5>
          <div className="space-y-1">
            {modulesList.slice(0, 2).map((module, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-sm text-gray-600">{module.title}</span>
              </div>
            ))}
            {modulesList.length > 2 && (
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span className="text-sm text-gray-500">
                  還有 {modulesList.length - 2} 個模組...
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Sample Formula */}
        {modulesList[0]?.examples?.[0]?.question && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h5 className="text-sm font-semibold text-gray-700 mb-2">範例問題:</h5>
            <div className="text-center text-blue-600 text-sm">
              {modulesList[0].examples[0].question.slice(0, 50)}...
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
          <div className="flex items-center space-x-1">
            <Clock className="w-4 h-4" />
            <span>{modulesList.length} 模組</span>
          </div>
          <div className="flex items-center space-x-1">
            <Award className="w-4 h-4" />
            <span>{modulesList.reduce((acc, module) => acc + (module.examples?.length || 0), 0)} 範例</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2">
          <button
            onClick={() => onUseTemplate(fullTopic || topic)}
            className="w-full flex items-center justify-center space-x-2 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>使用模板</span>
          </button>
          
          <div className="flex space-x-2">
            <button
              onClick={() => onGenerateFlashCards(fullTopic || topic)}
              className="flex-1 flex items-center justify-center space-x-2 bg-green-500 text-white py-2 px-3 rounded-lg hover:bg-green-600 transition-colors text-sm"
            >
              <Brain className="w-4 h-4" />
              <span>閃卡</span>
            </button>
            
            <button
              onClick={() => onStartQuiz(fullTopic || topic)}
              className="flex-1 flex items-center justify-center space-x-2 bg-red-500 text-white py-2 px-3 rounded-lg hover:bg-red-600 transition-colors text-sm"
            >
              <Target className="w-4 h-4" />
              <span>測驗</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MathTopicCard; 